// Routine rules (spec 4.4, N2, L11 to L13). Pure functions: the server places routines on new days,
// the client shows counters, "Nog deze week" and the af en toe list from the same rules.

import { addDays, isoWeekday, timeToMinutes } from "./time";
import { blockEnds, type SlotKind } from "./rhythm";

export type ScheduleType = "fixed" | "cadence" | "occasional";

export interface RoutineLike {
  id: string;
  title: string;
  schedule_type: ScheduleType;
  weekdays: number[];
  times_per_week: number | null;
  interval_days: number | null;
  slot_kinds: SlotKind[];
  duration_minutes: number;
  weekly_target: number | null;
  is_optional: boolean;
  last_done_on: string | null;
  dismissed_week: string | null;
  archived_at: string | Date | null;
  deleted_at: string | Date | null;
  position: number;
}

export interface RoutineEntryLike {
  routine_id: string | null;
  plan_date: string;
  status: string;
  deleted_at: string | Date | null;
}

export interface CompletionLike {
  routine_id: string;
  done_on: string;
  deleted_at: string | Date | null;
}

/** Monday of the ISO week of a date. */
export function weekStart(date: string): string {
  return addDays(date, 1 - isoWeekday(date));
}

export function sameWeek(a: string, b: string): boolean {
  return weekStart(a) === weekStart(b);
}

export function isLive(r: Pick<RoutineLike, "archived_at" | "deleted_at">): boolean {
  return !r.archived_at && !r.deleted_at;
}

function liveCompletions(completions: CompletionLike[], routineId: string) {
  return completions.filter((c) => !c.deleted_at && c.routine_id === routineId);
}

/** Check offs of a routine in the week of a date: the only source of a counter (L30). */
export function weekCount(routineId: string, completions: CompletionLike[], date: string): number {
  return liveCompletions(completions, routineId).filter((c) => sameWeek(c.done_on, date)).length;
}

/** Last time it was done: a check off, or the date set by hand, whichever is later. */
export function lastDone(routine: RoutineLike, completions: CompletionLike[]): string | null {
  return liveCompletions(completions, routine.id).reduce<string | null>(
    (max, c) => (!max || c.done_on > max ? c.done_on : max),
    routine.last_done_on,
  );
}

/** A block of a day as the placement sees it. `key` is whatever the caller uses to find it again. */
export interface SlotBlock {
  key: string;
  slot_kind: SlotKind;
  start_local: string;
  end_local: string | null;
  group_title: string | null;
}

export interface Placement {
  routineId: string;
  /** Null: "vandaag ergens", without a block. */
  blockKey: string | null;
}

export interface PlaceInput {
  date: string;
  /** Ziek, Vakantie: no routines at all (RO1 AC2, R4 AC3). */
  pausesRoutines: boolean;
  /** False for today or earlier (made late, e.g. after a missed nightly job): only fixed routines go there (RO2 AC1). */
  cadenceAllowed: boolean;
  blocks: SlotBlock[];
  /** Minutes already planned per block key (tasks and routines). */
  usedMinutes: Map<string, number>;
  routines: RoutineLike[];
  /** Routine entries on other days (planned or done); entries on `date` itself count as already placed. */
  entries: RoutineEntryLike[];
  completions: CompletionLike[];
}

function minutesOf(blocks: SlotBlock[]): Map<string, number> {
  const sorted = [...blocks].sort((a, b) => timeToMinutes(a.start_local) - timeToMinutes(b.start_local));
  const ends = blockEnds(sorted);
  return new Map(sorted.map((b, i) => [b.key, (ends[i] ?? 0) - timeToMinutes(b.start_local)]));
}

/**
 * Which routines go on a day that is being planned by the app (never a protected day, L13):
 *   fixed: on its weekdays, in the first block of a fitting kind; no such block on that day type: it lapses;
 *   cadence: while the week still needs one, never on the day after or before another time,
 *            in a fitting block where its duration still fits; otherwise it waits in "Nog deze week".
 * Steps of a group (Ochtend) are no place for a routine. Occasional routines are only proposed (RO3).
 */
export function placeRoutines(input: PlaceInput): Placement[] {
  if (input.pausesRoutines) return [];
  const blocks = input.blocks.filter((b) => b.slot_kind !== "work" && !b.group_title);
  const length = minutesOf(input.blocks);
  const used = new Map(input.usedMinutes);
  const out: Placement[] = [];
  const weekday = isoWeekday(input.date);
  const byStart = [...blocks].sort((a, b) => timeToMinutes(a.start_local) - timeToMinutes(b.start_local));
  const routines = input.routines.filter(isLive).sort((a, b) => a.position - b.position);

  const fitting = (r: RoutineLike, needRoom: boolean): SlotBlock | null | undefined => {
    if (!r.slot_kinds.length) return null;
    for (const kind of r.slot_kinds) {
      for (const b of byStart) {
        if (b.slot_kind !== kind) continue;
        if (needRoom && (length.get(b.key) ?? 0) - (used.get(b.key) ?? 0) < r.duration_minutes) continue;
        return b;
      }
    }
    return undefined;
  };

  for (const r of routines) {
    const entries = input.entries.filter((e) => e.routine_id === r.id && !e.deleted_at && (e.status === "planned" || e.status === "done"));
    if (entries.some((e) => e.plan_date === input.date)) continue;
    // "Geen gym" on this day (B24) is PK's choice: not placed again when the day is rebuilt (tester 5c bug 1).
    if (input.entries.some((e) => e.routine_id === r.id && !e.deleted_at && e.plan_date === input.date && e.status === "skipped")) continue;
    const done = liveCompletions(input.completions, r.id);
    if (done.some((c) => c.done_on === input.date)) continue;

    let block: SlotBlock | null | undefined;
    if (r.schedule_type === "fixed") {
      if (!r.weekdays.includes(weekday)) continue;
      block = fitting(r, false);
    } else if (r.schedule_type === "cadence") {
      if (!input.cadenceAllowed) continue;
      const target = r.times_per_week ?? 0;
      const weekDone = done.filter((c) => sameWeek(c.done_on, input.date)).length;
      const weekPlanned = entries.filter((e) => e.status === "planned" && sameWeek(e.plan_date, input.date)).length;
      if (weekDone + weekPlanned >= target) continue;
      const neighbours = new Set([addDays(input.date, -1), addDays(input.date, 1)]);
      if (entries.some((e) => neighbours.has(e.plan_date)) || done.some((c) => neighbours.has(c.done_on))) continue;
      block = fitting(r, true);
    } else {
      continue;
    }
    if (block === undefined) continue;
    if (block) used.set(block.key, (used.get(block.key) ?? 0) + r.duration_minutes);
    out.push({ routineId: r.id, blockKey: block?.key ?? null });
  }
  return out;
}

export interface StillThisWeek<R extends RoutineLike = RoutineLike> {
  routine: R;
  /** How many times it is still open this week. */
  remaining: number;
  /** Fixed routines: the earliest day it stood on and was missed (checking it off counts for that time). */
  missedOn: string | null;
}

/**
 * "Nog deze week" (N2): weekly routines that were missed, or can no longer get a place, until Sunday.
 * Never optional ones (RO2 AC6), never one with a counter (the gym is caught up by a proposal, RO4),
 * nothing after "Opnieuw beginnen" for this week, and on Monday it starts again.
 *
 * A cadence routine only counts as without a place once the app has made every day of the week
 * (`plannedThrough` is the last day of this week that exists): until then a later day may still get it (L71).
 * In a week with Ziek or Vakantie days only missed times count (R4 AC3).
 * Before that only times that stood on a passed day and were not done count.
 */
export function stillThisWeek<R extends RoutineLike>(
  routines: R[],
  entries: RoutineEntryLike[],
  completions: CompletionLike[],
  today: string,
  plannedThrough: string | null,
  isPaused: (date: string) => boolean = () => false,
): StillThisWeek<R>[] {
  const monday = weekStart(today);
  const weekMade = !!plannedThrough && plannedThrough >= addDays(monday, 6);
  // A week with Ziek or Vakantie days asks no catching up of a cadence (R4 AC3).
  const pausedWeek = Array.from({ length: 7 }, (_, i) => addDays(monday, i)).some(isPaused);
  const out: StillThisWeek<R>[] = [];
  for (const r of routines.filter(isLive).sort((a, b) => a.position - b.position)) {
    if (r.is_optional || r.weekly_target || r.schedule_type === "occasional" || r.dismissed_week === monday) continue;
    const mine = entries.filter((e) => e.routine_id === r.id && !e.deleted_at && sameWeek(e.plan_date, today));
    const done = weekCount(r.id, completions, today);
    // Check offs of this week that are not tied to a day where it stood (done from the list).
    const loose = Math.max(0, done - mine.filter((e) => e.status === "done").length);
    const missed = mine.filter((e) => e.plan_date < today && e.status === "planned").sort((x, y) => (x.plan_date < y.plan_date ? -1 : 1));
    let remaining: number;
    if (r.schedule_type === "fixed" || !weekMade || pausedWeek) {
      remaining = Math.max(0, missed.length - loose);
    } else {
      const ahead = mine.filter((e) => e.plan_date >= today && e.status === "planned").length;
      remaining = Math.max(0, (r.times_per_week ?? 0) - done - ahead);
    }
    if (remaining > 0)
      out.push({ routine: r, remaining, missedOn: r.schedule_type === "fixed" ? (missed[loose]?.plan_date ?? null) : null });
  }
  return out;
}

/** A cadence routine whose week is complete already: its remaining entries show "Al gedaan deze week" (RO2 AC5). */
export function weekComplete(routine: RoutineLike, completions: CompletionLike[], date: string): boolean {
  return routine.schedule_type === "cadence" && weekCount(routine.id, completions, date) >= (routine.times_per_week ?? Infinity);
}

export interface OccasionalItem<R extends RoutineLike = RoutineLike> {
  routine: R;
  lastDone: string | null;
  /** Days since it was due; negative: not due yet. Null: never done. */
  overdueDays: number | null;
}

function daysFrom(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** The af en toe list (RO3): most overdue first, relative to its interval; never done counts as due. */
export function occasionalList<R extends RoutineLike>(routines: R[], completions: CompletionLike[], today: string): OccasionalItem<R>[] {
  return routines
    .filter((r) => isLive(r) && r.schedule_type === "occasional")
    .map((routine) => {
      const last = lastDone(routine, completions);
      const interval = routine.interval_days ?? 1;
      return {
        routine,
        lastDone: last,
        overdueDays: last ? daysFrom(last, today) - interval : null,
        ratio: last ? daysFrom(last, today) / interval : Infinity,
      };
    })
    .sort((a, b) => b.ratio - a.ratio || a.routine.position - b.routine.position)
    .map(({ routine, lastDone: l, overdueDays }) => ({ routine, lastDone: l, overdueDays }));
}

/** Is it due (or over)? Used for proposals in the evening planning (wave 5) and the list styling. */
export function isDue(item: OccasionalItem): boolean {
  return item.overdueDays === null || item.overdueDays >= 0;
}

/**
 * The week goal of a routine with a counter (the gym): its target minus its days in that week that fall on a
 * day type that pauses routines (Ziek, Vakantie). Never below zero (RO4 AC6, R3 AC3, R4 AC4).
 */
export function weekTarget(
  routine: Pick<RoutineLike, "weekly_target" | "weekdays" | "schedule_type">,
  date: string,
  isPaused: (date: string) => boolean,
): number {
  const target = routine.weekly_target ?? 0;
  if (routine.schedule_type !== "fixed") return target;
  const monday = weekStart(date);
  const lost = routine.weekdays.filter((d) => isPaused(addDays(monday, d - 1))).length;
  return Math.max(0, target - lost);
}

/**
 * The gym streak (B39): how many weeks in a row the week goal was reached, counting back from last week; this week
 * counts as soon as it is reached too. A week with goal 0 (all Ziek or Vakantie) does not break the streak.
 */
export function weeksInRow(
  routine: Pick<RoutineLike, "id" | "weekly_target" | "weekdays" | "schedule_type">,
  completions: CompletionLike[],
  today: string,
  isPaused: (date: string) => boolean,
): number {
  if (!routine.weekly_target) return 0;
  const reached = (monday: string) => weekCount(routine.id, completions, monday) >= weekTarget(routine, monday, isPaused);
  const thisWeek = weekStart(today);
  let streak = reached(thisWeek) && weekTarget(routine, thisWeek, isPaused) > 0 ? 1 : 0;
  // Stop at the first week before any check off at all: no endless count over empty years.
  const first = liveCompletions(completions, routine.id).reduce<string | null>(
    (min, c) => (!min || c.done_on < min ? c.done_on : min),
    null,
  );
  if (!first) return streak;
  for (let monday = addDays(thisWeek, -7); monday >= weekStart(first); monday = addDays(monday, -7)) {
    if (weekTarget(routine, monday, isPaused) === 0) continue;
    if (!reached(monday)) break;
    streak++;
  }
  return streak;
}

export type IntervalUnit = "days" | "weeks" | "months";
const UNIT_DAYS: Record<IntervalUnit, number> = { days: 1, weeks: 7, months: 30 };

/** An "af en toe" interval as the form shows it (golf 9): whole months (30 days) or weeks when it fits, else days. */
export function intervalParts(days: number): { n: number; unit: IntervalUnit } {
  if (days >= 30 && days % 30 === 0) return { n: days / 30, unit: "months" };
  if (days >= 7 && days % 7 === 0) return { n: days / 7, unit: "weeks" };
  return { n: days, unit: "days" };
}

/** Back to days, within 1 to 365 (a year at most, L125). */
export function intervalDays(n: number, unit: IntervalUnit): number {
  return Math.min(365, Math.max(1, Math.round(n) * UNIT_DAYS[unit]));
}

/** "6 maanden", "2 weken", "10 dagen": how an "af en toe" interval reads in a list (golf 9). */
export function intervalLabel(days: number): string {
  const { n, unit } = intervalParts(days);
  const words = { days: ["dag", "dagen"], weeks: ["week", "weken"], months: ["maand", "maanden"] }[unit];
  return `${n} ${n === 1 ? words[0] : words[1]}`;
}
